import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useSiteContent, imgSrc } from "../lib/siteContent";

export default function PortfolioPage() {
  const { content } = useSiteContent();
  const [portfolio, setPortfolio] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio");
        setPortfolio(data);
      } catch (e) {
        // still renders
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const pp = content.portfolio_page;

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">{pp.eyebrow}</p>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tight max-w-4xl">
            {pp.title_pre} <span className="text-gradient-violet">{pp.title_accent}</span>
          </h1>
          <p className="text-[#5A5A72] mt-6 max-w-2xl">{pp.subtitle}</p>
        </div>
      </section>
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {portfolio.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {portfolio.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -6 }}
                  data-testid={`portfolio-item-${p.id}`}
                  className="group relative aspect-[4/5] overflow-hidden rounded-2xl border border-black/[0.06]"
                >
                  <img src={imgSrc(p.image)} alt={p.title} className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06060C] via-[#06060C]/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    {p.category && <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00D4FF] mb-1">{p.category}</p>}
                    <h3 className="font-display font-bold text-2xl text-white">{p.title}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : loaded ? (
            <div className="glass-card rounded-2xl p-14 text-center max-w-xl mx-auto" data-testid="portfolio-empty">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 bg-gradient-to-tr from-cyan-400/20 to-violet-400/20 grid place-items-center border border-[#00D4FF]/20">
                <Sparkles className="w-6 h-6 text-[#00D4FF]" />
              </div>
              <p className="font-display text-xl">Fresh work, coming soon.</p>
              <p className="text-[#6B7280] text-sm mt-2">We're curating our latest projects. In the meantime, let's talk about yours.</p>
              <Link to="/quote" className="mt-6 inline-flex btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold items-center gap-2">
                Start a project <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ) : null}
        </div>
      </section>
      <Footer />
    </div>
  );
}
